<template>
  <WdsSelect
    v-model="localModelValue"
    multi-select
    :info="fieldTyped.description ?? undefined"
    :required="required"
    :list="optionsList"
    :error="error"
    :disabled="
      props.forceDisabled || (props.forceEnabled ? false : !fieldTyped.viewerCanManageFieldValues)
    "
    searchable
  >
    <template #label>
      {{ fieldTyped.title }}
      <span v-if="required" class="tw-text-indigo-500 tw-font-bold">*</span>
      <WdsTag v-else sm class="tw-ml-1">{{ $t('Optional') }}</WdsTag>
      <CustomFieldVisibilityBadge
        :applies-to="fieldTyped.appliesTo"
        :shared-with-clients="fieldTyped.sharedWithClients"
        :shared-with-staffing-agencies="fieldTyped.sharedWithStaffingAgencies"
        :managed-elsewhere="!fieldTyped.viewerOwnsField"
        :owner-account-name="fieldTyped.account?.name"
      />
    </template>
  </WdsSelect>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsSelect, WdsTag } from '@worksome/wds-components'
import { CustomFieldTypeValueInput } from '@/gql/graphql'
import { useFragment } from '@/gql'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import useIsRequiredField from '@/components/Fieldable/composables/useIsRequiredField'
import { FieldableCustomFieldProps } from '@/components/Fieldable/types'
import { $t } from '@/composables/translation'
import CustomFieldVisibilityBadge from '@/components/Fieldable/CustomFieldVisibilityBadge.vue'

const emit = defineEmits(['update:model-value'])

const props = defineProps<FieldableCustomFieldProps>()

const fieldTyped = computed(() => useFragment(FieldableFormCustomFieldFragment, props.field))
const required = useIsRequiredField(props.field)

const optionsList = computed(() => {
  const values = [] as { value: string; label: string }[]

  fieldTyped.value.customFieldOptions.forEach((customFieldOption) =>
    values.push({
      value: customFieldOption.id,
      label: customFieldOption.value,
    }),
  )
  return values
})

const localModelValue = computed({
  get: () => {
    const selectedIds = props.modelValue?.multiSelect?.values ?? []
    return selectedIds
      .map((id) => {
        const option = fieldTyped.value.customFieldOptions?.find(
          (customFieldOption) => customFieldOption.id === id,
        )
        return option ? { value: option.id, label: option.value } : null
      })
      .filter(Boolean)
  },
  set: (selectedOptions) => {
    const values = !selectedOptions
      ? []
      : selectedOptions
          .filter((opt): opt is { value: string; label: string } => opt !== null)
          .map((opt) => opt.value)

    emit('update:model-value', {
      multiSelect: {
        id: fieldTyped.value.id,
        values,
      },
    } as CustomFieldTypeValueInput)
  },
})
</script>
